import React, { useEffect, useState } from 'react';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import './ValueRangeModal.scss';
import { CurrencyInput, Form } from '@availity/form';
import { Row, Col } from 'reactstrap';
import * as yup from 'yup';
import { useStateSelector } from '../../../../store/selectors';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';

interface ValueRangeModalProps {
    isRangeOfValueVisible: boolean;
    onClose: () => void;
    onRangeOfValueConfirm: (eventId: string, min: number, max: number) => void;
    selectedEventId?: string;
    selectedActionId?: number;
    isRangeOfValueReadonly?: boolean;
    minValue?: number;
    maxValue?: number;
}

const ValueRangeModal = (props: ValueRangeModalProps) => {
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );

    const validationSchema = yup.object().shape({
        minimum: yup
            .number()
            .required('Minimum value is required')
            .moreThan(0, 'Minimum must be greater than 0'),
        maximum: yup
            .number()
            .required('Maximum value is required')
            .moreThan(0, 'Maximum must be greater than 0')
            .moreThan(
                yup.ref('minimum'),
                'Maximum has to be bigger than Minimum'
            ),
    });

    const [valueOfRangeMin, setValueOfRangeMin] = useState(
        props.isRangeOfValueReadonly ? props.minValue.toString() ?? '0' : '0'
    );
    const [valueOfRangeMax, setValueOfRangeMax] = useState(
        props.isRangeOfValueReadonly ? props.maxValue.toString() ?? '0' : '0'
    );

    const [headerText, setHeaderText] = useState(
        'Preliminary Indicative Range of Value'
    );
    const [bodyText, setBodyText] = useState(
        'Please provide your indicative range of enterprise value for this opportunity. This valuation indication will be presented to the seller.'
    );
    const [buttonText, setButtonText] = useState('Confirm Range of Value');

    const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] =
        useState(false);

    const [initialFormValues, setInitialFormValues] = useState({
        minimum: valueOfRangeMin,
        maximum: valueOfRangeMax,
    });
    const [isFormLoading, setIsFormLoading] = useState(true);

    useEffect(() => {
        setInitialValueRangeFromPreviousExpressions();
    }, []);

    const setInitialValueRangeFromPreviousExpressions = () => {
        const expressionsWithRange =
            selectedOpportunity.buyer.actionState.filter(
                (item) => item.minPrice !== null && item.maxPrice !== null
            );
        const expressionsWithRangeExcludingCurrent = expressionsWithRange
            .filter((item) => item.actionId !== props.selectedActionId)
            .reverse();
        const previousValueOfRangeMin =
            expressionsWithRangeExcludingCurrent[0]?.minPrice?.toString();
        const previousValueOfRangeMax =
            expressionsWithRangeExcludingCurrent[0]?.maxPrice?.toString();

        if (!props.isRangeOfValueReadonly) {
            const updatedMin = previousValueOfRangeMin ?? '0';
            const updatedMax = previousValueOfRangeMax ?? '0';
            setValueOfRangeMin(updatedMin);
            setValueOfRangeMax(updatedMax);
            setInitialFormValues({
                minimum: updatedMin,
                maximum: updatedMax,
            });
        }
        const firstValueRange = expressionsWithRange[0] ?? null;
        if (
            expressionsWithRangeExcludingCurrent.length > 0 &&
            firstValueRange.actionId !== props.selectedActionId
        ) {
            setHeaderText('Updated Indicative Range of Value');
            setBodyText(
                'Please update your preliminary indicative range of value for this opportunity. This updated valuation indication will be presented to the seller.'
            );
            setButtonText('Confirm Updated Range of Value');
        }
        setIsFormLoading(false);
    };

    const handleRangeOfValueConfirm = (
        values: { minimum: string; maximum: string },
        resetForm: (values: {
            values: { minimum: string; maximum: string };
        }) => void
    ) => {
        if (props.selectedEventId) {
            props.onRangeOfValueConfirm(
                props.selectedEventId,
                Number(values.minimum),
                Number(values.maximum)
            );
            createNotification(
                'Range of Value has been successfully confirmed.',
                'success'
            );
        }
        resetForm({
            values: initialFormValues,
        });
    };

    return (
        <>
            <Rodal
                width={550}
                height={500}
                visible={props.isRangeOfValueVisible}
                animation={'slideRight'}
                showCloseButton={true}
                onClose={props.onClose}
                showMask={false}
                className={'range-of-value-modal'}>
                <ModalHeader>{headerText}</ModalHeader>
                <ModalBody>
                    <div className="range-of-value-text-container">
                        <span className="range-of-value-text">{bodyText}</span>
                    </div>
                    <div className="range-of-value-label-container">
                        <span className="range-of-value-label">
                            {'Range of Value:'}
                        </span>
                    </div>

                    <div className="range-of-value-form">
                        {isFormLoading ? (<></>):(
                        <Form
                            initialValues={initialFormValues}
                            validationSchema={validationSchema}
                            onSubmit={() => {}}>
                            <Row>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="mr-2 range-label">
                                            {'Minimum: '}
                                        </div>
                                        <CurrencyInput
                                            name={'minimum'}
                                            disabled={
                                                props.isRangeOfValueReadonly
                                            }
                                            value={valueOfRangeMin.toString()}
                                            allowDecimals={false}
                                            allowNegativeValue={false}
                                            prefix="$"
                                            onValueChanged={(value) => {
                                                const newValue = value
                                                    ? value.replace('.00', '')
                                                    : '0';

                                                setValueOfRangeMin(newValue);
                                            }}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="mr-2 range-label">
                                            {'Maximum: '}
                                        </div>
                                        <CurrencyInput
                                            name={'maximum'}
                                            disabled={
                                                props.isRangeOfValueReadonly
                                            }
                                            value={valueOfRangeMax.toString()}
                                            allowDecimals={false}
                                            allowNegativeValue={false}
                                            prefix="$"
                                            onValueChanged={(value) => {
                                                const newValue = value
                                                    ? value.replace('.00', '')
                                                    : '0';

                                                setValueOfRangeMax(newValue);
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <div className="range-of-value-buttons">
                                <button
                                    type="button"
                                    className="btn btn-cancel"
                                    onClick={() => {
                                        props.onClose();
                                    }}>
                                    {'Close'}
                                </button>
                                <button
                                    disabled={props.isRangeOfValueReadonly}
                                    type="submit"
                                    onClick={async () => {
                                        const isValid =
                                            await validationSchema.isValid({
                                                minimum: valueOfRangeMin,
                                                maximum: valueOfRangeMax,
                                            });

                                        if (isValid) {
                                            setIsConfirmationPopupVisible(true);
                                        }
                                    }}
                                    className="btn btn-primary">
                                    {buttonText}
                                </button>
                            </div>
                        </Form>
                        )}
                    </div>
                </ModalBody>
            </Rodal>
            {isConfirmationPopupVisible && (
                <ConfirmationModal
                    header={buttonText}
                    isVisible={isConfirmationPopupVisible}
                    setIsVisible={setIsConfirmationPopupVisible}
                    confirmationText="Are you sure you want to confirm this indicative range of value for the opportunity?"
                    params={''}
                    onConfirm={() => {
                        handleRangeOfValueConfirm(
                            {
                                minimum: valueOfRangeMin,
                                maximum: valueOfRangeMax,
                            },
                            () => {}
                        );
                    }}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                />
            )}
        </>
    );
};

export default ValueRangeModal;
